import { subMonths, format } from 'date-fns';

import NwApiSdk from '@nerdwallet/nw-api-sdk';

import { createAsyncAction } from '@lib/redux/utils';
import { ISO_DATE_FORMAT } from '@constants/dates';

import {
  transformFinancialDataProfileToLegacyDataShape,
  convertDaySpendingListToDateSpendingMap,
} from './helpers/utils';
import {
  FINANCIAL_TRANSACTIONS_PROJECTION_FOR_MONTH,
  FINANCIAL_TRANSACTIONS_TOP_CATEGORIES_SPENDING,
  FINANCIAL_TRANSACTIONS_AVERAGE_SPENDING_BY_DAY_OF_THE_MONTH,
  FINANCIAL_TRANSACTIONS_SPENDING_BY_DATE_FOR_MONTH,
  GET_FINANCIAL_DATA_PROFILE_FOR_USER,
  FINANCIAL_TRANSACTIONS_TOP_MERCHANT_SPENDING,
  FINANCIAL_TRANSACTIONS_SPENDING_AND_EARNING_FOR_MONTH_RANGE,
} from './helpers/queries';
import { TransactionsFilter } from '../types';
import mapKeysDeep from '../utils/map-keys-deep';

/**
 * Fetches the "YODLEE_PROFILE" profile, which contains Plaid financial account data.
 */
export const readYodleeProfile = createAsyncAction('readYodleeProfile', () => ({
  makeRequest: async (_state, { apolloClient }) => {
    const financialDataResponse = await apolloClient.query({
      query: GET_FINANCIAL_DATA_PROFILE_FOR_USER,
      fetchPolicy: 'network-only',
    });
    const data = financialDataResponse?.data;

    return {
      data: transformFinancialDataProfileToLegacyDataShape(data),
      profileType: NwApiSdk.IDENTITY_PROFILES.YODLEE_PROFILE,
    };
  },
  meta: { profileType: NwApiSdk.IDENTITY_PROFILES.YODLEE_PROFILE },
}));

export interface GetCashFlowProjectionAsyncAction {
  args: {
    dayInTargetMonth: Date;
  };
  payload: {
    projectedEarning: Record<string, any>;
  };
  meta: Pick<GetCashFlowProjectionAsyncAction['args'], 'dayInTargetMonth'>;
}

/**
 * Makes a request to Identity to fetch the projected (estimated) income,
 * which is based on:
 *
 * (1) the user's self-reported monthly_net_income, or
 * (2) the user's self-reported annual_income / 12, or
 * (2) an average of their earning for the past two months
 * */
export const getCashFlowProjection =
  createAsyncAction<GetCashFlowProjectionAsyncAction>(
    'getCashFlowProjection',
    ({ dayInTargetMonth }) => ({
      makeRequest: async (_state, { apolloClient }) => {
        if (!dayInTargetMonth) {
          throw new Error('no day in target month supplied');
        }

        const cashFlowProjectionResult = await apolloClient.query({
          query: FINANCIAL_TRANSACTIONS_PROJECTION_FOR_MONTH,
          variables: {
            dateInTargetMonth: format(dayInTargetMonth, ISO_DATE_FORMAT),
          },
          fetchPolicy: 'network-only',
        });
        const { earning } =
          cashFlowProjectionResult?.data
            ?.financialTransactionsProjectionForMonth ?? {};

        return {
          projectedEarning: earning,
        };
      },
      meta: {
        dayInTargetMonth,
      },
    }),
  );

export interface GetCategoryWindowRollupsAsyncAction {
  args: {
    endDate: Date;
    startDate: Date;
    today?: Date;
  };
  payload: {
    window: Record<'sum_amount' | 'category_v2' | 'category', any>;
  };
  meta: Pick<
    GetCategoryWindowRollupsAsyncAction['args'],
    'endDate' | 'startDate' | 'today'
  >;
}

/**
 * Fetches an ordered list of spending categories for a given startDate and endDate.
 */
export const getCategoryWindowRollups =
  createAsyncAction<GetCategoryWindowRollupsAsyncAction>(
    'getCategoryWindowRollups',
    ({ endDate, startDate, today }) => ({
      makeRequest: async (_state, { apolloClient }) => {
        if (!startDate || !endDate) {
          throw new Error('no start or end date supplied');
        }

        const response = await apolloClient.query({
          query: FINANCIAL_TRANSACTIONS_TOP_CATEGORIES_SPENDING,
          fetchPolicy: 'network-only',
          variables: {
            dateInTargetStartMonth: format(startDate, ISO_DATE_FORMAT),
            dateInTargetEndMonth: format(endDate, ISO_DATE_FORMAT),
          },
        });

        const topCategories =
          response?.data?.financialTransactionsTopCategoriesBySpending
            ?.topCategoriesBySpending;

        return {
          window: mapKeysDeep(topCategories, {
            category: 'category_v2',
            amount: 'sum_amount',
            __typename: undefined,
          }),
        };
      },
      meta: {
        endDate,
        startDate,
        today,
      },
    }),
  );

export interface GetDailyAverageTransactionsCashFlowAsyncAction {
  args: {
    dayInEndMonth: Date;
    dayInStartMonth: Date;
  };
  payload: {
    averageSpendingPerDay: Record<string, number>;
  };
  meta: Pick<
    GetDailyAverageTransactionsCashFlowAsyncAction['args'],
    'dayInEndMonth' | 'dayInStartMonth'
  >;
}

/**
 * Makes a request to Identity to fetch the net total of transaction amounts for
 * each day in the target and previous months.
 * */
export const getDailyAverageTransactionsCashFlow =
  createAsyncAction<GetDailyAverageTransactionsCashFlowAsyncAction>(
    'getDailyAverageTransactionsCashFlow ',
    ({ dayInEndMonth, dayInStartMonth }) => ({
      makeRequest: async (_state, { apolloClient }) => {
        if (!dayInStartMonth || !dayInEndMonth) {
          throw new Error('no day in start or end month supplied');
        }

        const response = await apolloClient.query({
          query: FINANCIAL_TRANSACTIONS_AVERAGE_SPENDING_BY_DAY_OF_THE_MONTH,
          variables: {
            dateInTargetEndMonth: format(dayInEndMonth, ISO_DATE_FORMAT),
            dateInTargetStartMonth: format(dayInStartMonth, ISO_DATE_FORMAT),
          },
          fetchPolicy: 'network-only',
        });

        const averageSpendingByDayOfTheMonthList =
          response?.data?.financialTransactionsAverageSpendingByDayOfTheMonth
            ?.averageSpendingByDayOfTheMonth ?? [];

        return {
          averageSpendingPerDay: averageSpendingByDayOfTheMonthList.reduce(
            (acc, { dayOfTheMonth, amount }) => ({
              ...acc,
              [String(dayOfTheMonth)]: amount,
            }),
            {},
          ),
        };
      },
      meta: {
        dayInEndMonth,
        dayInStartMonth,
      },
    }),
  );

export interface GetDailyTransactionsCashFlowAsyncAction {
  args: {
    dayInTargetMonth: Date;
  };
  payload: {
    previousMonthSpending: Record<string, number>;
    targetMonthSpending: Record<string, number>;
  };
  meta: Pick<
    GetDailyTransactionsCashFlowAsyncAction['args'],
    'dayInTargetMonth'
  >;
}

/**
 * Makes a request to Identity to fetch the net total of transaction amounts for
 * each day in the target month AND previous month.
 * */
export const getDailyTransactionsCashFlow =
  createAsyncAction<GetDailyTransactionsCashFlowAsyncAction>(
    'getDailyTransactionsCashFlow',
    ({ dayInTargetMonth }) => ({
      makeRequest: async (_state, { apolloClient }) => {
        if (!dayInTargetMonth) {
          throw new Error('no day in target month supplied');
        }

        const response = await apolloClient.query({
          fetchPolicy: 'network-only',
          query: FINANCIAL_TRANSACTIONS_SPENDING_BY_DATE_FOR_MONTH,
          variables: {
            dateInTargetMonth: format(dayInTargetMonth, ISO_DATE_FORMAT),
          },
        });

        const data =
          response?.data?.financialTransactionsSpendingByDateForMonth ?? {};
        const {
          spendingByDayOfTheMonthForPriorMonth,
          spendingByDayOfTheMonthForTargetMonth,
        } = data;

        return {
          previousMonthSpending: convertDaySpendingListToDateSpendingMap(
            spendingByDayOfTheMonthForPriorMonth,
            subMonths(dayInTargetMonth, 1),
          ),
          targetMonthSpending: convertDaySpendingListToDateSpendingMap(
            spendingByDayOfTheMonthForTargetMonth,
            dayInTargetMonth,
          ),
        };
      },
      meta: {
        dayInTargetMonth,
      },
    }),
  );

export interface GetTopMerchantsAsyncAction {
  args: {
    endDate: Date;
    startDate: Date;
    limit: number;
  };
  payload: {
    window: Record<string, any>;
  };
  meta: Pick<GetTopMerchantsAsyncAction['args'], 'endDate' | 'startDate'>;
}

/**
 * Makes a request to Identity to fetch an ordered list of top merchants based
 * on the slices you provide. If one of the slices is 'window', you MUST
 * include startDate and endDate. The limit param defines at most how many
 * merchants we will get back
 */
export const getTopMerchants = createAsyncAction<GetTopMerchantsAsyncAction>(
  'getTopMerchants',
  ({ endDate, startDate, limit }) => ({
    makeRequest: async (_state, { apolloClient }) => {
      if (!startDate || !endDate) {
        throw new Error('no start or end date supplied');
      }

      const response = await apolloClient.query({
        query: FINANCIAL_TRANSACTIONS_TOP_MERCHANT_SPENDING,
        fetchPolicy: 'network-only',
        variables: {
          dateInTargetStartMonth: format(startDate, ISO_DATE_FORMAT),
          dateInTargetEndMonth: format(endDate, ISO_DATE_FORMAT),
          pagination: {
            limit,
          },
        },
      });

      const topMerchants =
        response?.data?.financialTransactionsTopMerchantsBySpending
          ?.topMerchantsBySpending;

      return {
        window: mapKeysDeep(topMerchants, {
          merchantName: 'canonical_merchant_name',
          amount: 'sum_amount',
          __typename: undefined,
        }),
      };
    },
    meta: {
      startDate,
      endDate,
    },
  }),
);

export interface GetTransactionsMonthCashFlowAsyncAction {
  args: {
    dayInTargetMonth: Date;
    today: Date;
    filter?: TransactionsFilter;
  };
  payload: {
    earning: number;
    spending: number;
  };
  meta: Pick<
    GetTransactionsMonthCashFlowAsyncAction['args'],
    'dayInTargetMonth' | 'filter'
  >;
}

/**
 * Makes a request to Identity to fetch cash flow for the given month.
 */
export const getTransactionsMonthCashFlow =
  createAsyncAction<GetTransactionsMonthCashFlowAsyncAction>(
    'getTransactionsMonthCashFlow',
    ({ dayInTargetMonth, today, filter = {} }) => ({
      makeRequest: async (_state, { apolloClient }) => {
        if (!dayInTargetMonth) {
          throw new Error('no day in target month supplied');
        }

        const result = await apolloClient.query({
          query: FINANCIAL_TRANSACTIONS_SPENDING_AND_EARNING_FOR_MONTH_RANGE,
          fetchPolicy: 'network-only',
          variables: {
            dateInStartMonth: format(dayInTargetMonth, ISO_DATE_FORMAT),
            dateInEndMonth: format(today || dayInTargetMonth, ISO_DATE_FORMAT),
            filter: {
              accountId: filter.accountId,
              excludeHiddenTransactions: true,
              includeDisabledAccounts: !!filter.accountId,
              includeTags: filter.tag && [filter.tag],
              merchantName: filter.merchantName,
            },
          },
        });
        const { earning, spending } =
          result?.data?.financialTransactionsSpendingAndEarningForMonthRange
            ?.spendingAndEarningForMonthRange ?? {};

        return {
          earning,
          spending,
        };
      },
      meta: {
        dayInTargetMonth,
        filter,
      },
    }),
  );
